import React, { useMemo, useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css'; // Essential for map rendering
import { FamilyMember } from '../types';
import { MapPin, Navigation, Calendar, X, Users } from 'lucide-react';

interface FamilyMapProps {
  members: Record<string, FamilyMember>;
  onViewMember?: (id: string) => void;
}

// Detect user's country and return coordinates
const getUserCountryCoords = async (): Promise<[number, number]> => {
  try {
    // Try browser geolocation first
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve([position.coords.latitude, position.coords.longitude]);
        },
        () => {
          // Fallback: Use IP-based geolocation
          fetch('https://ipapi.co/json/')
            .then(res => res.json())
            .then(data => {
              if (data.latitude && data.longitude) {
                resolve([data.latitude, data.longitude]);
              } else {
                resolve([20, 0]); // Default world view
              }
            })
            .catch(() => resolve([20, 0])); // Default world view
        }
      );
    });
  } catch (err) {
    console.error('Geolocation error:', err);
    return [20, 0]; // Default world view
  }
};
const geoCache: Record<string, [number, number]> = {};

// Get coordinates for any location in the world
const getCoords = async (location: string): Promise<[number, number] | null> => {
  if (!location) return null;
  
  // Check cache first
  if (geoCache[location]) return geoCache[location];
  
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(location)}&format=json&limit=1`,
      { headers: { 'Accept': 'application/json' } }
    );
    const data = await response.json();
    
    if (data && data[0]) {
      const coords: [number, number] = [parseFloat(data[0].lat), parseFloat(data[0].lon)];
      geoCache[location] = coords;
      return coords;
    }
  } catch (err) {
    console.error('Geocoding error:', err);
  }
  
  // Fallback: Generate stable random coordinates for unknown locations
  let hash = 0;
  for (let i = 0; i < location.length; i++) hash = location.charCodeAt(i) + ((hash << 5) - hash);
  const lat = -85 + ((Math.abs(hash) % 170));
  const lng = -180 + ((Math.abs(hash) % 360));
  return [lat, lng];
};

// --- 2. CUSTOM AVATAR MARKER WITH BADGE ---
const createAvatarIcon = (url: string, count: number = 1) => {
  return L.divIcon({
    className: 'custom-avatar-marker',
    html: `<div style="position: relative; width: 48px; height: 48px;">
      <div style="
        width: 48px; 
        height: 48px; 
        border-radius: 50%; 
        border: 3px solid white; 
        box-shadow: 0 4px 10px rgba(0,0,0,0.3); 
        background-image: url('${url}'); 
        background-size: cover; 
        background-position: center;
        background-color: #f3f4f6;
      "></div>
      <div style="
        position: absolute;
        top: -6px;
        right: -6px;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background-color: #4f46e5;
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        font-weight: bold;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        border: 2px solid white;
        cursor: pointer;
      " class="location-badge" data-count="${count}">${count}</div>
    </div>`,
    iconSize: [48, 48],
    iconAnchor: [24, 24], // Center of the marker
    popupAnchor: [0, -24]
  });
};

const FamilyMap: React.FC<FamilyMapProps> = ({ members, onViewMember }) => {
  // Safety check - if members are missing, show loading state
  const [activeMember, setActiveMember] = useState<FamilyMember | null>(null);
  const [mapCenter, setMapCenter] = useState<[number, number]>([20, 0]); // Default world view
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [locationMembers, setLocationMembers] = useState<FamilyMember[]>([]);

  // --- 3. DATA PREPARATION ---
  const [mapData, setMapData] = useState<{ markers: any[]; lines: any[] }>({ markers: [], lines: [] });
  const [isLoadingCoords, setIsLoadingCoords] = useState(false);

  // Get user's country on mount
  useEffect(() => {
    getUserCountryCoords().then(coords => setMapCenter(coords));
  }, []);

  // Fetch coordinates asynchronously for all members
  useEffect(() => {
    if (!members || Object.keys(members).length === 0) {
      setMapData({ markers: [], lines: [] });
      return;
    }

    setIsLoadingCoords(true);
    
    (async () => {
      const markers: any[] = [];
      const lines: any[] = [];
      const memberList = Object.values(members);

      for (let index = 0; index < memberList.length; index++) {
        const member = memberList[index];
        
        // Filter: Only show alive members with location details
        if (member.isDeceased || !member.location || member.location.trim() === '') {
          continue;
        }
        
        const memberLocation = member.location;
        
        const coords = await getCoords(memberLocation);
        
        if (coords) {
          markers.push({
            ...member,
            coords,
            displayLocation: memberLocation
          });

          // Add Migration Lines (Connect to Parents)
          if (member.parentIds && member.parentIds.length > 0) {
            for (const parentId of member.parentIds) {
              const parent = members[parentId];
              if (parent) {
                const parentLocation = parent.location || `Member with id ${parentId}`;
                const parentCoords = await getCoords(parentLocation);
                if (parentCoords) {
                  lines.push({
                    from: parentCoords,
                    to: coords,
                    color: '#6366f1'
                  });
                }
              }
            }
          }
        }
      }

      setMapData({ markers, lines });
      setIsLoadingCoords(false);
    })();
  }, [members]);

  // Create location count map
  const locationCountMap = useMemo(() => {
    const counts: Record<string, number> = {};
    mapData.markers.forEach(member => {
      const loc = member.displayLocation;
      counts[loc] = (counts[loc] || 0) + 1;
    });
    return counts;
  }, [mapData.markers]);

  // Handle location badge click
  const handleLocationClick = (location: string) => {
    const membersAtLocation = mapData.markers
      .filter(m => m.displayLocation === location)
      .sort((a, b) => a.name.localeCompare(b.name));
    setLocationMembers(membersAtLocation);
    setSelectedLocation(location);
  };

  // Safety check - if members are missing, show loading state
  if (!members || Object.keys(members).length === 0 || isLoadingCoords) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-indigo-100 mx-auto mb-4 flex items-center justify-center">
            <div className="w-8 h-8 rounded-full border-2 border-indigo-300 border-t-indigo-600 animate-spin"></div>
          </div>
          <p className="text-slate-600 font-bold">{isLoadingCoords ? 'Geocoding locations...' : 'Loading map...'}</p>
        </div>
      </div>
    );
  }

  return (
    // UPDATED: Changed background to light gray
    <div className="w-full h-screen relative bg-gray-50">
      
      {/* MAP HEADER OVERLAY */}
      <div className="absolute top-4 left-4 z-[400] bg-white/90 backdrop-blur-md p-4 rounded-2xl shadow-xl border border-white/20 max-w-xs">
        <h2 className="text-lg font-black text-gray-900 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-indigo-600 fill-current" /> Ancestral Atlas
        </h2>
        <p className="text-xs text-gray-600 mt-1">
          Visualizing the migration of {mapData.markers.length} family members across generations.
        </p>
        <div className="flex gap-4 mt-3">
          <div className="flex items-center gap-1 text-[10px] font-bold text-indigo-600">
             <div className="w-8 h-1 bg-indigo-500 rounded-full"></div> Migration
          </div>
          <div className="flex items-center gap-1 text-[10px] font-bold text-gray-600">
             <div className="w-3 h-3 rounded-full bg-white border-2 border-gray-400 shadow-sm"></div> Current
          </div>
        </div>
      </div>

      <MapContainer 
        center={mapCenter}
        zoom={mapCenter[0] === 20 && mapCenter[1] === 0 ? 2 : 5}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
        className="z-0"
      >
        {/* UPDATED: LIGHT MODE MAP TILES (CartoDB Positron) */}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        />

        {/* 1. MIGRATION LINES */}
        {mapData.lines.map((line, idx) => (
          <Polyline 
            key={`line-${idx}`} 
            positions={[line.from, line.to]} 
            pathOptions={{ 
              color: line.color, 
              weight: 2, 
              opacity: 0.6, 
              dashArray: '5, 10', // Dashed line for "Movement"
              className: 'animate-pulse' 
            }} 
          />
        ))}

        {/* 2. AVATAR MARKERS */}
        {mapData.markers.map((member) => (
          <Marker 
            key={member.id} 
            position={member.coords}
            icon={createAvatarIcon(
              member.avatarUrl || `https://ui-avatars.com/api/?name=${member.name}&background=random`,
              locationCountMap[member.displayLocation] || 1
            )}
            eventHandlers={{
              click: (e) => {
                // Check if badge was clicked
                const target = e.originalEvent.target as HTMLElement;
                if (target.classList.contains('location-badge')) {
                  handleLocationClick(member.displayLocation);
                } else {
                  setActiveMember(member);
                }
              },
            }}
          />
        ))}

      </MapContainer>

      {/* 3. PROFILE DRAWER (Floating Card) */}
      {activeMember && (
        <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 bg-white rounded-3xl p-5 shadow-2xl z-[500] animate-slide-up border border-gray-100">
           <div className="flex gap-4 items-center">
              <img 
                src={activeMember.avatarUrl || `https://ui-avatars.com/api/?name=${activeMember.name}`} 
                className="w-16 h-16 rounded-full border-4 border-indigo-50 shadow-md object-cover bg-gray-100" 
              />
              <div>
                 <h3 className="text-xl font-black text-gray-900 leading-tight">{activeMember.name}</h3>
                 <div className="flex items-center gap-1 text-indigo-600 text-xs font-bold mt-1">
                    <MapPin className="w-3 h-3" /> {activeMember.displayLocation || activeMember.location || "Location Unknown"}
                 </div>
                 {activeMember.occupation ? (
                   <div className="flex items-center gap-1 text-gray-400 text-xs font-medium mt-0.5">
                      {activeMember.occupation}
                   </div>
                 ) : (
                   <div className="flex items-center gap-1 text-gray-400 text-xs font-medium mt-0.5">
                      Profession Unknown
                   </div>
                 )}
              </div>
           </div>
           
           <div className="mt-4 grid grid-cols-2 gap-2">
             <button 
               onClick={() => {
                 setActiveMember(null);
                 if (onViewMember && activeMember) {
                   onViewMember(activeMember.id);
                 }
               }}
               className="py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-bold shadow-lg shadow-indigo-200 hover:bg-indigo-700 transition-all flex items-center justify-center gap-2"
             >
               <Navigation className="w-4 h-4" /> View
             </button>
             <button 
               onClick={() => setActiveMember(null)}
               className="py-2.5 bg-gray-100 text-gray-600 rounded-xl text-sm font-bold hover:bg-gray-200 transition-all"
             >
               Close
             </button>
           </div>
        </div>
      )}

      {/* 4. LOCATION MEMBERS MODAL */}
      {selectedLocation && (
        <div className="fixed inset-0 bg-black/50 z-[500] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full max-h-[80vh] flex flex-col animate-fade-in border border-gray-100">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white rounded-t-3xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                  <Users className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">{selectedLocation}</h3>
                  <p className="text-xs text-gray-500">{locationMembers.length} member{locationMembers.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <button 
                onClick={() => setSelectedLocation(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Members List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {locationMembers.map((member) => (
                <div key={member.id} className="p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer group">
                  <div className="flex items-start gap-3">
                    <img 
                      src={member.avatarUrl || `https://ui-avatars.com/api/?name=${member.name}&background=random`}
                      alt={member.name}
                      className="w-12 h-12 rounded-full border-2 border-white shadow-md object-cover bg-gray-200"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-gray-900 text-sm group-hover:text-indigo-600 transition-colors">{member.name}</h4>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {member.gender && `${member.gender} • `}
                        {member.dob ? `Born ${member.dob}` : 'Age unknown'}
                      </p>
                      {member.occupation && (
                        <p className="text-xs text-indigo-600 font-medium mt-1">{member.occupation}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-100 bg-gray-50 rounded-b-3xl">
              <button 
                onClick={() => setSelectedLocation(null)}
                className="w-full py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-bold hover:bg-indigo-700 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 5. EMPTY STATE MESSAGE */}
      {mapData.markers.length === 0 && (
        <div className="absolute inset-0 z-[300] flex items-center justify-center bg-black/5 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-8 shadow-2xl text-center max-w-xs">
            <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-1">No Locations Found</h3>
            <p className="text-sm text-gray-500">
              Add location information to living family members to see them on the map.<br />
              <span className="text-xs text-slate-500 block mt-2">
                Only active (living) members with location details are shown.
              </span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FamilyMap;